import { FC, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import type {
  ScoringType,
  SignalStrengthBand,
  TopicsFilter,
} from "@/lib/scoring/types";
import type { CachedTopic } from "@/types/intent";
import { IntentTopicsSection } from "./IntentTopicsSection";
import { getHumanMinMax } from "./MinMaxEditor";

const MAX_SIGNAL_STRENGTH = 100;

const DEFAULT_TOPICS: TopicsFilter = {
  ids: [],
  inclusionMethod: "ANY_PRESENT",
};

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: SignalStrengthBand = {
  signalScore: {},
  score: 0,
  topics: DEFAULT_TOPICS,
};

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as SignalStrengthBand[];
  if (typed.length === 0) return true;
  const topicIds = typed[0]?.topics?.ids ?? [];
  if (topicIds.length === 0) return true;
  return typed.some((band, i) => {
    const others = typed.filter((_, j) => j !== i);
    return getBandError(band, others) !== null;
  });
}

export function getBandSummary(band: unknown): string {
  const b = band as SignalStrengthBand;
  return getHumanMinMax(b.signalScore?.min, b.signalScore?.max);
}

// ── Validation ────────────────────────────────────────────────────────────

function outOfRange(v: number | undefined): boolean {
  return v !== undefined && (v < 0 || v > MAX_SIGNAL_STRENGTH);
}

function getBandError(
  band: SignalStrengthBand,
  others: SignalStrengthBand[]
): string | null {
  if (!band.score) return "Points can't be empty";

  const { min, max } = band.signalScore ?? {};
  if (min === undefined && max === undefined) return "Range is empty";
  if (outOfRange(min) || outOfRange(max))
    return `Value must be 0–${MAX_SIGNAL_STRENGTH}`;
  if (min !== undefined && max !== undefined && min > max)
    return "Min must be ≤ max";

  const rangeMin = min ?? -Infinity;
  const rangeMax = max ?? Infinity;
  if (
    others.some((o) => {
      const oMin = o.signalScore?.min ?? -Infinity;
      const oMax = o.signalScore?.max ?? Infinity;
      return rangeMin < oMax && oMin < rangeMax;
    })
  )
    return "Intervals intersect";

  return null;
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const SignalStrengthEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.signalStrength ?? [];

  const [topicsCache, setTopicsCache] = useState<CachedTopic[]>([]);
  const [topics, setTopics] = useState<TopicsFilter>(
    bands[0]?.topics ?? DEFAULT_TOPICS
  );

  const propagateTopics = useCallback(
    (newTopics: TopicsFilter, newBands: SignalStrengthBand[]) => {
      onChange({
        ...criterion,
        signalStrength: newBands.map((b) => ({ ...b, topics: newTopics })),
      });
    },
    [criterion, onChange]
  );

  const handleTopicsChange = useCallback(
    (newTopics: TopicsFilter, cache: CachedTopic[]) => {
      setTopics(newTopics);
      setTopicsCache(cache);
      propagateTopics(newTopics, bands);
    },
    [bands, propagateTopics]
  );

  const updateBand = (index: number, patch: Partial<SignalStrengthBand>) => {
    const newBands = bands.map((b, i) =>
      i === index ? { ...b, ...patch } : b
    );
    onChange({
      ...criterion,
      signalStrength: newBands.map((b) => ({ ...b, topics })),
    });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      signalStrength: bands
        .filter((_, i) => i !== index)
        .map((b) => ({ ...b, topics })),
    });
  };

  const addBand = () => {
    onChange({
      ...criterion,
      signalStrength: [...bands, { signalScore: {}, score: 0, topics }],
    });
  };

  return (
    <Box sx={{ px: 2 }}>
      <IntentTopicsSection
        topics={topics}
        topicsCache={topicsCache}
        onChange={handleTopicsChange}
      />

      {bands.map((band, i) => {
        const others = bands.filter((_, j) => j !== i);
        const isDirty =
          band.signalScore?.min !== undefined ||
          band.signalScore?.max !== undefined ||
          band.score !== 0;
        const error = isDirty ? getBandError(band, others) : null;
        return (
          <Box key={i} sx={{ mb: error ? 0.5 : 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr 72px auto",
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Min Intensity"
                value={band.signalScore?.min ?? ""}
                error={!!error && error !== "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    signalScore: {
                      ...band.signalScore,
                      min:
                        e.target.value === ""
                          ? undefined
                          : Number(e.target.value),
                    },
                  })
                }
                inputProps={{
                  step: 1,
                  min: 0,
                  max: MAX_SIGNAL_STRENGTH,
                  "data-testid": "scoring-min-intensity-input",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                —
              </Typography>
              <TextField
                size="small"
                type="number"
                label="Max Intensity"
                value={band.signalScore?.max ?? ""}
                error={!!error && error !== "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    signalScore: {
                      ...band.signalScore,
                      max:
                        e.target.value === ""
                          ? undefined
                          : Number(e.target.value),
                    },
                  })
                }
                inputProps={{
                  step: 1,
                  min: 0,
                  max: MAX_SIGNAL_STRENGTH,
                  "data-testid": "scoring-max-intensity-input",
                }}
              />
              <TextField
                size="small"
                type="number"
                label="Pts"
                value={band.score === 0 ? "" : band.score}
                error={error === "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.25, mb: 0.75 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600, mt: 0.5 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
